import CompanyReviewScript from '@v2/shared/components/CompanyReviewScript';
import useIntersectionObserver from '@v2/shared/lib/useIntersectionObserver';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * How far ahead of the section the loader is fetched. Enough that the widget has
 * usually painted by the time the section is scrolled to, and not so much that a
 * visitor who never reaches it pays for a third-party script.
 */
const ROOT_MARGIN = '400px';

/**
 * SmartCustomer's Review Showcase, the body of the reviews section of /guarantee.
 *
 * A different widget from the homepage's carousel but the same account and the same
 * loader, so the only thing that differs is the container class — see
 * CompanyReviewScript.
 *
 * The section around this, its heading and its `#reviews` anchor belong to the page, the
 * way every other section of it does. What is here is the part that is this widget's:
 * the mount point, the gate in front of the loader, and the note that says who collected
 * the reviews.
 *
 * The container renders from the first paint and the loader does not: the section sits
 * below several screens of copy, and a visitor who stops reading before it should not
 * have fetched a third-party script. Mounting CompanyReviewScript only once the section
 * is near also keeps the ordering the widget needs — the container is already in the
 * DOM when the script runs, so there is nothing for it to miss.
 *
 * The `noscript` fallback carries no rating and no review count, and must not be given
 * them. The live figures exist only inside the widget's own JavaScript, so anything
 * written here is a copy that nothing updates: it would be right on the day it shipped
 * and quietly wrong afterwards, in the one place a visitor cannot see the real widget
 * disagreeing with it. A link is always true, so the link is the whole fallback.
 *
 * It is written as markup rather than as children because a browser with scripting
 * enabled parses `noscript` content as text, so React would hydrate against a text node
 * where it rendered elements. That alone is not enough, and this is the part that is easy
 * to get wrong: the browser hands the same escaped text back as `innerHTML`, so React
 * sees the prop it set as `dangerouslySetInnerHTML` come back different and logs a
 * mismatch anyway. `suppressHydrationWarning` is the documented way to say the difference
 * is the point. Caught by `var/ssr/hydration-check.mjs`, which is the only check that
 * sees this — the page looks correct either way.
 */
export default function ReviewShowcase() {
  const { buildUrl } = useUrlGenerator();
  const [ref, isInView] = useIntersectionObserver({ rootMargin: ROOT_MARGIN });
  const reviews = buildUrl('/reviews');

  return (
    <>
      <div ref={ref}>
        {isInView ? <CompanyReviewScript /> : null}
        <div className="stjr-review-showcase max-w-full" />
      </div>

      <noscript
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: `<p><a href="${reviews}">Read our verified customer reviews</a>.</p>`,
        }}
      />

      <p className="mb-0 mt-4 text-sm text-slate-500">
        Every review above is collected and verified by SmartCustomer, an independent platform,
        directly from confirmed customers after purchase. We cannot edit, reorder, or remove a
        review, and the one-star ones sit right next to the five-star ones.{' '}
        <a className="text-primary-700 underline underline-offset-2" href={reviews}>
          Read them all
        </a>
        .
      </p>
    </>
  );
}
